import { FormControl, FormLabel } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import type React from 'react';

import { PagePaths, ReportEnrichmentTrackingEvents } from '@revelio/core';
import {
  Callout,
  InfoTipIcon,
  RevelioTooltip,
  Text,
  Switch as UiSwitch,
} from '@revelio/ui';

import { dashMetaFetch } from '../../../shared/api/dash-meta-fetch';
import { RoleTaxonomyIdSelect } from '../../../shared/components/RoleTaxonomyIdSelect';
import { useToast } from '../../../shared/hooks/useToast';
import { CardListSelectController } from '../../../shared/ui/card-list';
import { DeliverableStepContainer } from '../deliverable-step-container';
import { DeliverablePaths } from '../deliverables-page';
import ConfirmationMappedColumns from '../file-upload/confirmation-mapped-columns';
import { useFileUploadStore } from '../file-upload/file-upload-context';
import styles from './confirmation-step.module.css';
import {
  DEFAULT_TAXONOMY_ID,
  selectEnrichmentJobTaxonomySettings,
  selectEnrichmentModals,
  useDataEnrichmentActions,
  useDataEnrichmentSelector,
} from './data-enrichment.store';

const INCLUDE_V2_TOOLTIP =
  "For granularities Role k1500 and below, your file will include columns for both v2 and v3, Role k10 will map to to Job Taxonomy v2's k7. Granularities above Role k1500 will appear only for Job Taxonomy v3.";

export const JobTaxonomyMenuHeader = () => {
  const { setEnrichmentJobTaxonomySettings } = useDataEnrichmentActions();
  const jobTaxonomySettings = useDataEnrichmentSelector(
    selectEnrichmentJobTaxonomySettings
  );

  return (
    <div className={styles.taxonomyHeader}>
      <FormControl flex="1" maxW="375px">
        <RoleTaxonomyIdSelect
          value={jobTaxonomySettings.taxonomyId}
          includeAdaptive={false}
          includeCustom={false}
          variant="default"
          usePortal={false}
          selectedLabelPrefix="Taxonomy ID"
          onChange={(taxonomyId: number) =>
            setEnrichmentJobTaxonomySettings({
              ...jobTaxonomySettings,
              taxonomyId,
            })
          }
        />
      </FormControl>
      <FormControl
        className={styles.switchControl}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        maxW="170px"
      >
        <FormLabel
          className={styles.switchLabel}
          fontSize="sm"
          color="gray.600"
          mb="0"
          pr={2}
        >
          Include V2 Roles{' '}
          <RevelioTooltip
            label={INCLUDE_V2_TOOLTIP}
            openDelay={300}
            maxWidth="260px"
          >
            <InfoTipIcon size={12} />
          </RevelioTooltip>
        </FormLabel>
        <UiSwitch
          checked={jobTaxonomySettings.includeV2}
          onChange={(event) =>
            setEnrichmentJobTaxonomySettings({
              ...jobTaxonomySettings,
              includeV2: event.target.checked,
            })
          }
        />
      </FormControl>
    </div>
  );
};

const ConfirmationStep = ({
  loading,
  setLoading,
}: {
  onSubmit?: (arg0: () => void) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    mappedColumns,
    fileType,
    selectedSheet,
    headerIndex,
    goToNext,
    goToPrevious,
    clientName,
    s3FileLocation,
    email,
    oryId,
  } = useFileUploadStore((state) => ({
    mappedColumns: state.mappedColumns ?? [],
    fileType: state.fileType ?? 'csv',
    selectedSheet: state.selectedSheet ?? '',
    headerIndex: state.headerIndex ?? '',
    goToNext: state.goToNext,
    goToPrevious: state.goToPrevious,
    clientName: state.clientName ?? '',
    s3FileLocation: state.s3FileLocation ?? '',
    email: state.email ?? '',
    oryId: state.oryId ?? '',
  }));
  const toast = useToast();
  const { setEnrichmentModals } = useDataEnrichmentActions();

  const handleOnSubmit = async () => {
    if (selectedOptions.length < 1) {
      toast({
        status: 'error',
        variant: 'left-accent',
        position: 'top-right',
        title: 'No columns selected!',
        description: 'You must select at least one or more columns!',
        isClosable: true,
      });
      return;
    }

    const mappedColumnsToObject = mappedColumns?.reduce<{
      [key: string]: string;
    }>((acc, obj) => {
      const key = Object.keys(obj)[0];
      // @ts-expect-error noUncheckedIndexedAccess
      const value = obj[key];
      // @ts-expect-error noUncheckedIndexedAccess
      acc[key] = value;
      return acc;
    }, {});

    const uploadMetadata = async (id: string) => {
      const metaData = {
        client: clientName,
        s3_data_location: s3FileLocation,
        email: email,
        columns: mappedColumnsToObject,
        models: selectedOptions,
        sheet: fileType === 'excel' ? selectedSheet : '',
        header: headerIndex,
        taxonomy_id: jobTaxonomySettings?.taxonomyId ?? DEFAULT_TAXONOMY_ID,
        include_v2: jobTaxonomySettings?.includeV2 ?? false,
      };
      const response = await dashMetaFetch(
        `/api/deliverables/dataenrichment/${id}/upload/metadata`,
        {
          method: 'POST',
          body: JSON.stringify(metaData),
        }
      );
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      return response.json();
    };

    if (oryId) {
      setLoading(true);
      try {
        await uploadMetadata(oryId);
        mixpanel.track(
          ReportEnrichmentTrackingEvents.DATA_ENRICHMENT_SUBMITTED,
          {
            model_count: selectedOptions.length,
            taxonomy_id: jobTaxonomySettings?.taxonomyId ?? DEFAULT_TAXONOMY_ID,
            include_v2_roles: jobTaxonomySettings?.includeV2 ?? false,
            mapped_columns_count: Object.keys(mappedColumnsToObject).length,
            page_type: 'data_enrichment',
          }
        );
        goToNext();
      } catch {
        toast({
          status: 'error',
          variant: 'left-accent',
          position: 'top-right',
          title: 'Submission failed',
          description:
            'Something went wrong while submitting your enrichment. Please try again.',
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const modelRequirements = {
    location: ['location'],
    job_taxonomy: ['title'],
    seniority: ['title'],
    salary_base: ['title'],
    salary_total: ['title'],
    contractor_pay_rates: ['title'],
    contractor_pay_rates_2: ['location'],
    company_mapping: ['company_name'],
  };

  const getIsMandatoryValue = (model: string) =>
    !mappedColumns?.some((obj) =>
      Object.values(obj).some((value) =>
        modelRequirements[model as keyof typeof modelRequirements].includes(
          value
        )
      )
    );

  const selectedOptions = useDataEnrichmentSelector(selectEnrichmentModals);
  const jobTaxonomySettings = useDataEnrichmentSelector(
    selectEnrichmentJobTaxonomySettings
  );

  const items = [
    {
      heading: null,
      columns: [
        {
          id: 'location',
          label: 'Location',
          menuItems: [
            { id: 'region', label: 'Region' },
            { id: 'country', label: 'Country' },
            { id: 'state', label: 'State' },
            { id: 'city', label: 'City' },
            { id: 'metro_area', label: 'Metropolitan Area' },
          ],
          isMandatory: getIsMandatoryValue('location'),
        },
        {
          id: 'job_taxonomy',
          label: 'Job Taxonomy',
          menuHeaderContent: <JobTaxonomyMenuHeader />,
          menuItems: [
            { id: 'role_k10', label: 'Role k10' },
            { id: 'role_k50', label: 'Role k50' },
            { id: 'role_k150', label: 'Role k150' },
            { id: 'role_k1000', label: 'Role k1000' },
            { id: 'role_k1250', label: 'Role k1250' },
            { id: 'role_k1500', label: 'Role k1500' },
            { id: 'role_k5000', label: 'Role k5000' },
            { id: 'role_k10000', label: 'Role k10000' },
            { id: 'role_k17000', label: 'Role k17000' },
            { id: 'confidence', label: 'Confidence' },
          ],
          isMandatory: getIsMandatoryValue('job_taxonomy'),
        },
        {
          id: 'seniority',
          label: 'Seniority',
          isMandatory: getIsMandatoryValue('seniority'),
        },
        {
          id: 'salary_base',
          label: 'Base Salary',
          menuItems: [
            { id: 'average_base', label: 'Base Average' },
            { id: '10th_percentile_base', label: 'Base 10th Percentile' },
            { id: '25th_percentile_base', label: 'Base 25th Percentile' },
            { id: '50th_percentile_base', label: 'Base 50th Percentile' },
            { id: '75th_percentile_base', label: 'Base 75th Percentile' },
            { id: '90th_percentile_base', label: 'Base 90th Percentile' },
          ],
          isMandatory: getIsMandatoryValue('salary_base'),
        },
        {
          id: 'salary_total',
          label: 'Total Compensation',
          menuItems: [
            { id: 'average_total', label: 'Total Average' },
            { id: '10th_percentile_total', label: 'Total 10th Percentile' },
            { id: '25th_percentile_total', label: 'Total 25th Percentile' },
            { id: '50th_percentile_total', label: 'Total 50th Percentile' },
            { id: '75th_percentile_total', label: 'Total 75th Percentile' },
            { id: '90th_percentile_total', label: 'Total 90th Percentile' },
          ],
          isMandatory: getIsMandatoryValue('salary_total'),
        },
        {
          id: 'contractor_pay_rates',
          label: 'Contractor Pay Rate',
          menuItems: [
            { id: 'average_contract', label: 'Contractor Pay Average' },
            {
              id: '10th_percentile_contract',
              label: 'Contract 10th Percentile',
            },
            {
              id: '25th_percentile_contract',
              label: 'Contract 25th Percentile',
            },
            {
              id: '50th_percentile_contract',
              label: 'Contract 50th Percentile',
            },
            {
              id: '75th_percentile_contract',
              label: 'Contract 75th Percentile',
            },
            {
              id: '90th_percentile_contract',
              label: 'Contract 90th Percentile',
            },
          ],
          isMandatory:
            getIsMandatoryValue('contractor_pay_rates') ||
            getIsMandatoryValue('contractor_pay_rates_2'),
        },
        {
          id: 'company_mapping',
          label: 'Company Mapping',
          menuItems: [
            { id: 'rcid', label: 'RCID' },
            {
              id: 'ultimate_parent_rcid',
              label: 'Ultimate Parent RCID',
            },
            {
              id: 'primary_company_name',
              label: 'Primary Parent RCID',
            },
            {
              id: 'ultimate_primary_company_name',
              label: 'Ultimate Primary Parent RCID',
            },
            {
              id: 'company_linkedin',
              label: 'Company LinkedIn',
            },
            {
              id: 'ultimate_company_linkedin',
              label: 'Ultimate Company LinkedIn',
            },
          ],
          isMandatory: getIsMandatoryValue('company_mapping'),
        },
      ],
    },
  ];

  const hasMandatory = items.some((item) => {
    return item.columns.some((prop) => prop.isMandatory);
  });

  return (
    <DeliverableStepContainer
      id="confirm_enrichment"
      title="Confirm Enrichment"
      submitButtonContent="Submit"
      startOverPath={`/${PagePaths.DELIVERABLES}/${DeliverablePaths.DATASET}`}
      onSubmit={handleOnSubmit}
      disableFade
      isSubmitLoading={loading}
      isDataBuilder={false}
    >
      <div className={styles.sections}>
        <div className={styles.mappedColumnsSection}>
          <ConfirmationMappedColumns mappedColumns={mappedColumns} />
        </div>
        <div className={styles.enrichmentSection}>
          <div className={styles.enrichmentContent}>
            <Text className={styles.sectionHeading} as="h2" fontSize="md">
              Which columns would like to be added to your file?
            </Text>
            {hasMandatory && (
              <Callout variant="warning">
                Some of the columns have been disabled due no related assigned
                columns were mapped.{' '}
                <button
                  type="button"
                  className={styles.calloutAction}
                  onClick={() => goToPrevious()}
                >
                  Update
                </button>{' '}
                your mapping if you think this is a mistake.
              </Callout>
            )}
            <CardListSelectController
              variant="columns"
              items={items}
              value={selectedOptions}
              hideCtaIfNotSelected={true}
              gridProps={{
                spacing: 4,
                columns: 3,
                gridAutoFlow: 'row',
              }}
              onChange={(value: string[]) => setEnrichmentModals(value)}
            />
          </div>
        </div>
      </div>
    </DeliverableStepContainer>
  );
};

export default ConfirmationStep;
